import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { PlusIcon } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import useUnidades from '@/hooks/useUnidades';
import {
  conversaoUnidadeKeys,
  createConversao,
  deleteConversao,
  fetchConversoes,
  updateConversao,
} from '@/services/conversaoUnidadeService';
import { fetchProdutosERP, produtoKeys } from '@/services/produtoService';
import {
  converterUnidade,
  createUnidadeMedida,
  deleteUnidadeMedida,
  fetchUnidadesMedidaCadastradas,
  unidadeMedidaKeys,
} from '@/services/unidadeMedidaService';
import { parseApiError } from '@/utils/apiError';
import { groupUnidadesMedida, mergeUnidadesMedida } from '@/utils/unidadesMedida';
import { conversaoFormSchema, novaUnidadeFormSchema, type TConversaoFormData, type TNovaUnidadeFormData } from './schema';
import type { IUnidade } from '@/types/unidade';
import type { IConversaoUnidade } from '@/types/unidadeMedida';
import type { IProdutoERP } from '@/types/produto';

export const Route = createFileRoute('/_authenticated/conversao-unidade-medida/')({
  component: ConversaoUnidadeMedida,
});

const GRUPOS_DISPONIVEIS = ['Massa', 'Volume', 'Quantidade', 'Comprimento/Área'];

const emptyConversaoValues: TConversaoFormData = {
  codigo_produto: '',
  descricao_produto: '',
  unidade_origem: '',
  unidade_destino: '',
  fator_conversao: '',
  unidade: '',
};

const emptyNovaUnidadeValues: TNovaUnidadeFormData = {
  value: '',
  label: '',
  grupo: '',
  unidade: '',
};

function getUnidadeBadgeClass(unidade: string | undefined): string {
  const normalizedUnidade = unidade?.toLowerCase() || '';
  const known = ['kg', 'g', 'l', 'ml', 'un', 'cx', 'pc', 'm'];
  const base = 'rounded-full px-2.5 py-1 text-xs font-medium';
  if (known.includes(normalizedUnidade)) return `${base} bg-primary text-on-primary`;
  return `${base} bg-surface-container text-on-surface-variant`;
}

function ConversaoUnidadeMedida() {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tableFilter, setTableFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [showModal, setShowModal] = useState(false);
  const [editingConversao, setEditingConversao] = useState<IConversaoUnidade | null>(null);
  const [deleteConversaoConfirmId, setDeleteConversaoConfirmId] = useState<string | null>(null);

  const [produtoSearchTerm, setProdutoSearchTerm] = useState('');

  const [showModalUnidade, setShowModalUnidade] = useState(false);
  const [showModalGerenciarUnidades, setShowModalGerenciarUnidades] = useState(false);
  const [deleteUnidadeConfirm, setDeleteUnidadeConfirm] = useState<{ value: string; label: string } | null>(null);

  const [valorConverter, setValorConverter] = useState('');
  const [resultadoConversao, setResultadoConversao] = useState<{ valor_convertido: number; fator_utilizado: number } | null>(null);
  const [loadingConversao, setLoadingConversao] = useState(false);

  const conversaoForm = useForm<TConversaoFormData>({
    resolver: zodResolver(conversaoFormSchema),
    mode: 'onChange',
    defaultValues: emptyConversaoValues,
  });
  const {
    control: conversaoControl,
    register: registerConversao,
    handleSubmit: handleConversaoSubmit,
    reset: resetConversaoForm,
    setValue: setConversaoValue,
    getValues: getConversaoValues,
    formState: { errors: conversaoErrors, isValid: isConversaoValid },
  } = conversaoForm;

  const novaUnidadeForm = useForm<TNovaUnidadeFormData>({
    resolver: zodResolver(novaUnidadeFormSchema),
    mode: 'onChange',
    defaultValues: emptyNovaUnidadeValues,
  });
  const {
    control: novaUnidadeControl,
    register: registerNovaUnidade,
    handleSubmit: handleNovaUnidadeSubmit,
    reset: resetNovaUnidadeForm,
    setValue: setNovaUnidadeValue,
    getValues: getNovaUnidadeValues,
    formState: { errors: novaUnidadeErrors, isValid: isNovaUnidadeValid },
  } = novaUnidadeForm;

  const watchedUnidadeOrigem = useWatch({ control: conversaoControl, name: 'unidade_origem' });
  const watchedUnidadeDestino = useWatch({ control: conversaoControl, name: 'unidade_destino' });
  const watchedDescricaoProduto = useWatch({ control: conversaoControl, name: 'descricao_produto' });

  const conversionInputsKey = `${valorConverter}|${watchedUnidadeOrigem}|${watchedUnidadeDestino}`;
  const [previousConversionInputsKey, setPreviousConversionInputsKey] = useState(conversionInputsKey);
  if (conversionInputsKey !== previousConversionInputsKey) {
    setPreviousConversionInputsKey(conversionInputsKey);
    if (valorConverter && watchedUnidadeOrigem && watchedUnidadeDestino && watchedUnidadeOrigem === watchedUnidadeDestino) {
      setResultadoConversao({ valor_convertido: parseFloat(valorConverter), fator_utilizado: 1 });
    } else {
      setResultadoConversao(null);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: unidades,
    loading: unidadesLoading,
    error: unidadesError,
  } = useUnidades({ skip: 0, limit: 100 });

  const singleUnidadeSelectLocked = unidades.length === 1 && !unidadesLoading && !unidadesError;

  useEffect(() => {
    if (!singleUnidadeSelectLocked || !showModal) return;
    const onlyId = String(unidades[0].id);
    if (getConversaoValues('unidade') !== onlyId) {
      setConversaoValue('unidade', onlyId);
    }
  }, [showModal, singleUnidadeSelectLocked, unidades, getConversaoValues, setConversaoValue]);

  useEffect(() => {
    if (!singleUnidadeSelectLocked || !showModalUnidade) return;
    const onlyId = String(unidades[0].id);
    if (getNovaUnidadeValues('unidade') !== onlyId) {
      setNovaUnidadeValue('unidade', onlyId);
    }
  }, [showModalUnidade, singleUnidadeSelectLocked, unidades, getNovaUnidadeValues, setNovaUnidadeValue]);

  const conversoesParams = { page: currentPage, page_size: pageSize, search: debouncedSearch };
  const {
    data: conversoesData,
    isLoading,
    isError,
    error,
    refetch: refetchConversoes,
  } = useQuery({
    queryKey: conversaoUnidadeKeys.list(conversoesParams),
    queryFn: () => fetchConversoes(conversoesParams),
  });
  const conversoes = conversoesData?.items ?? [];
  const totalPages = conversoesData?.total_pages ?? 1;
  const totalCount = conversoesData?.total_count ?? 0;

  const { data: unidadesMedidaData } = useQuery({
    queryKey: unidadeMedidaKeys.cadastradas,
    queryFn: fetchUnidadesMedidaCadastradas,
  });
  const unidadesCadastradas = useMemo(() => unidadesMedidaData?.items ?? [], [unidadesMedidaData]);

  const unidadesMedida = useMemo(() => mergeUnidadesMedida(unidadesCadastradas), [unidadesCadastradas]);
  const unidadesMedidaAgrupadas = useMemo(() => groupUnidadesMedida(unidadesMedida), [unidadesMedida]);

  const produtosErpParams = { page: 1, page_size: 100 };
  const { data: produtosErpData, isLoading: loadingProdutosERP } = useQuery({
    queryKey: produtoKeys.erp(produtosErpParams),
    queryFn: () => fetchProdutosERP(produtosErpParams),
  });
  const produtosERPList = useMemo(() => produtosErpData?.items ?? [], [produtosErpData]);

  const createMutation = useMutation({ mutationFn: createConversao });
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof updateConversao>[1] }) => updateConversao(id, payload),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteConversao,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: conversaoUnidadeKeys.all }),
  });

  const criarUnidadeMutation = useMutation({
    mutationFn: createUnidadeMedida,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: unidadeMedidaKeys.cadastradas }),
  });
  const excluirUnidadeMutation = useMutation({
    mutationFn: deleteUnidadeMedida,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: unidadeMedidaKeys.cadastradas }),
  });

  useEffect(() => {
    if (!valorConverter || !watchedUnidadeOrigem || !watchedUnidadeDestino) return;
    if (watchedUnidadeOrigem === watchedUnidadeDestino) return;

    const timer = setTimeout(async () => {
      try {
        setLoadingConversao(true);
        const data = await converterUnidade({
          valor: parseFloat(valorConverter),
          unidade_origem: watchedUnidadeOrigem,
          unidade_destino: watchedUnidadeDestino,
        });
        setResultadoConversao(data);
      } catch {
        setResultadoConversao(null);
      } finally {
        setLoadingConversao(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [valorConverter, watchedUnidadeOrigem, watchedUnidadeDestino]);

  const produtosFiltrados = useMemo(() => {
    if (!produtoSearchTerm) return produtosERPList;
    const termLower = produtoSearchTerm.toLowerCase();
    return produtosERPList.filter(
      (p) => p.sku?.toLowerCase().includes(termLower) || p.descricao?.toLowerCase().includes(termLower),
    );
  }, [produtosERPList, produtoSearchTerm]);

  const buscaFiltrados = useMemo(() => {
    if (!searchTerm) return produtosERPList;
    const termLower = searchTerm.toLowerCase();
    return produtosERPList.filter(
      (p) => p.sku?.toLowerCase().includes(termLower) || p.nome?.toLowerCase().includes(termLower),
    );
  }, [produtosERPList, searchTerm]);

  const handleSelectProduto = (produto: IProdutoERP | null) => {
    if (!produto) return;
    setConversaoValue('codigo_produto', produto.sku || '', { shouldValidate: true });
    setConversaoValue('descricao_produto', produto.descricao || produto.nome || '');
    if (produto.unidade_medida) setConversaoValue('unidade_origem', produto.unidade_medida, { shouldValidate: true });
    setProdutoSearchTerm(produto.sku || '');
  };

  const resetForm = () => {
    resetConversaoForm(emptyConversaoValues);
    setProdutoSearchTerm('');
    setEditingConversao(null);
    setValorConverter('');
    setResultadoConversao(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeConversaoModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleEdit = (conversao: IConversaoUnidade) => {
    setEditingConversao(conversao);
    setConversaoValue('codigo_produto', conversao.codigo_produto, { shouldValidate: true });
    setConversaoValue('descricao_produto', conversao.descricao_produto);
    setConversaoValue('unidade_origem', conversao.unidade_origem, { shouldValidate: true });
    setConversaoValue('unidade_destino', conversao.unidade_destino, { shouldValidate: true });
    setConversaoValue('fator_conversao', String(conversao.fator_conversao), { shouldValidate: true });
    setProdutoSearchTerm(conversao.codigo_produto);
    setShowModal(true);
  };

  const confirmDeleteConversao = async () => {
    if (deleteConversaoConfirmId == null) return;
    try {
      await deleteMutation.mutateAsync(deleteConversaoConfirmId);
      toast.success('Conversão excluída com sucesso.');
    } catch (err) {
      toast.error(parseApiError(err, 'Erro ao excluir conversão'));
    } finally {
      setDeleteConversaoConfirmId(null);
    }
  };

  const onSubmitConversao = async (formData: TConversaoFormData) => {
    const payload = {
      codigo_produto: formData.codigo_produto,
      descricao_produto: formData.descricao_produto || formData.codigo_produto,
      unidade_origem: formData.unidade_origem,
      unidade_destino: formData.unidade_destino,
      fator_conversao: parseFloat(formData.fator_conversao),
      unidade: formData.unidade,
    };

    try {
      if (editingConversao) {
        await updateMutation.mutateAsync({ id: editingConversao.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      toast.success(editingConversao ? 'Conversão atualizada com sucesso.' : 'Conversão criada com sucesso.');
      closeConversaoModal();
      await queryClient.invalidateQueries({ queryKey: conversaoUnidadeKeys.all });
    } catch (err) {
      toast.error(parseApiError(err, 'Erro ao salvar conversão'));
    }
  };

  const closeNovaUnidadeModal = () => {
    setShowModalUnidade(false);
    resetNovaUnidadeForm(emptyNovaUnidadeValues);
  };

  const onSubmitNovaUnidade = async (formData: TNovaUnidadeFormData) => {
    try {
      await criarUnidadeMutation.mutateAsync({
        value: formData.value,
        label: formData.label,
        grupo: formData.grupo,
        unidade: formData.unidade,
      });
      toast.success('Unidade de medida criada com sucesso.');
      closeNovaUnidadeModal();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (typeof detail === 'string' && (detail.includes('duplicate') || detail.includes('already exists'))) {
        toast.error(`A unidade "${formData.value}" já existe!`);
      } else {
        toast.error(parseApiError(err, 'Erro ao cadastrar unidade'));
      }
    }
  };

  const podeExcluirUnidade = (unidadeMedida: { value: string }): boolean =>
    unidadesCadastradas.some((u) => u.value === unidadeMedida.value);

  const confirmExcluirUnidade = async () => {
    if (!deleteUnidadeConfirm) return;
    const unidadeCadastrada = unidadesCadastradas.find((u) => u.value === deleteUnidadeConfirm.value);
    if (!unidadeCadastrada?.id) {
      toast.error('Esta unidade é padrão do sistema e não pode ser excluída.');
      setDeleteUnidadeConfirm(null);
      return;
    }
    try {
      await excluirUnidadeMutation.mutateAsync(unidadeCadastrada.id);
      toast.success('Unidade excluída com sucesso.');
    } catch (err) {
      toast.error(parseApiError(err, 'Erro ao excluir unidade'));
    } finally {
      setDeleteUnidadeConfirm(null);
    }
  };

  const conversoesFiltradas = conversoes.filter(
    (c) =>
      !tableFilter ||
      c.codigo_produto?.toLowerCase().includes(tableFilter.toLowerCase()) ||
      c.descricao_produto?.toLowerCase().includes(tableFilter.toLowerCase()),
  );

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-2xl font-semibold text-on-surface">Conversão de Unidades de Medida</h2>

      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
        <Field className="w-64">
          <FieldLabel htmlFor="buscaProduto">Buscar Produto</FieldLabel>
          <Combobox
            items={buscaFiltrados}
            filter={null}
            inputValue={searchTerm}
            onInputValueChange={setSearchTerm}
            value={null}
            onValueChange={(produto: IProdutoERP | null) => {
              if (produto) setSearchTerm(produto.sku || '');
            }}
            itemToStringLabel={(produto: IProdutoERP) => produto.sku ?? ''}
          >
            <ComboboxInput
              id="buscaProduto"
              placeholder="Digite para buscar produto…"
              autoComplete="off"
            />
            <ComboboxContent>
              {loadingProdutosERP ? (
                <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                  <Spinner /> Carregando produtos ERP…
                </div>
              ) : (
                <>
                  <ComboboxEmpty>Nenhum produto encontrado</ComboboxEmpty>
                  <ComboboxList>
                    {(produto: IProdutoERP) => (
                      <ComboboxItem
                        key={produto.sku}
                        value={produto}
                      >
                        <span className="font-medium text-primary">{produto.sku}</span> <span>{produto.nome}</span>
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </>
              )}
            </ComboboxContent>
          </Combobox>
        </Field>

        <Field>
          <FieldLabel htmlFor="pageSize">Itens por página</FieldLabel>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(1);
            }}
          >
            <SelectTrigger id="pageSize">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>

        <Button onClick={() => void refetchConversoes()}>Buscar</Button>
        <Button
          onClick={openCreateModal}
          className="bg-success hover:bg-success/90"
        >
          Nova Conversão
        </Button>
        <Button
          variant="outline"
          className="ml-auto"
          onClick={() => setShowModalGerenciarUnidades(true)}
        >
          Gerenciar Unidades
        </Button>
      </div>

      <div className="text-sm text-on-surface-variant">
        Total de registros: <strong className="text-on-surface">{totalCount}</strong>
      </div>

      {isLoading ? (
        <div className="p-8 text-center">
          <LoadingSpinner text="Carregando conversões…" />
        </div>
      ) : isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-lg bg-error-container p-8 text-on-error-container"
        >
          <p>{parseApiError(error, 'Erro ao buscar conversões de unidade')}</p>
          <Button onClick={() => void refetchConversoes()}>Tentar novamente</Button>
        </div>
      ) : conversoes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-outline-variant p-8">
          <p className="text-on-surface-variant">Nenhuma conversão de unidade encontrada.</p>
          <Button
            onClick={openCreateModal}
            className="bg-success hover:bg-success/90"
          >
            Criar primeira conversão
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-outline-variant p-4">
            <div className="mb-4 flex items-center gap-3">
              <Input
                type="text"
                placeholder="Filtrar por código ou descrição…"
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                autoComplete="off"
                className="w-72"
              />
              <span className="text-sm text-on-surface-variant">Filtre os registros pelo código ou parte da descrição</span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código Produto</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Unidade Origem</TableHead>
                    <TableHead>Unidade Destino</TableHead>
                    <TableHead>Fator de Conversão</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conversoesFiltradas.map((conversao) => (
                    <TableRow key={conversao.id}>
                      <TableCell>
                        <strong>{conversao.codigo_produto}</strong>
                      </TableCell>
                      <TableCell>{conversao.descricao_produto}</TableCell>
                      <TableCell>
                        <span className={getUnidadeBadgeClass(conversao.unidade_origem)}>{conversao.unidade_origem}</span>
                      </TableCell>
                      <TableCell>
                        <span className={getUnidadeBadgeClass(conversao.unidade_destino)}>{conversao.unidade_destino}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{conversao.fator_conversao}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(conversao)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeleteConversaoConfirmId(conversao.id)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <span className="text-sm text-on-surface-variant">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Próxima
            </Button>
          </div>
        </>
      )}

      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          if (!open) closeConversaoModal();
        }}
        modal={false}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingConversao ? 'Editar Conversão' : 'Nova Conversão de Unidade'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={(e) => void handleConversaoSubmit(onSubmitConversao)(e)}>
            <FieldGroup>
              <Field data-invalid={!!conversaoErrors.codigo_produto}>
                <FieldLabel htmlFor="codigoProduto">Código do Produto *</FieldLabel>
                <Combobox
                  items={produtosFiltrados}
                  filter={null}
                  inputValue={produtoSearchTerm}
                  onInputValueChange={(value) => {
                    setProdutoSearchTerm(value);
                    setConversaoValue('codigo_produto', value, { shouldValidate: true });
                  }}
                  value={null}
                  onValueChange={handleSelectProduto}
                  itemToStringLabel={(produto: IProdutoERP) => produto.sku ?? ''}
                >
                  <ComboboxInput
                    id="codigoProduto"
                    placeholder="Digite para buscar produto…"
                    autoComplete="off"
                    aria-invalid={!!conversaoErrors.codigo_produto}
                  />
                  <ComboboxContent>
                    {loadingProdutosERP ? (
                      <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                        <Spinner /> Carregando produtos…
                      </div>
                    ) : (
                      <>
                        <ComboboxEmpty>Nenhum produto encontrado</ComboboxEmpty>
                        <ComboboxList>
                          {(produto: IProdutoERP) => (
                            <ComboboxItem
                              key={produto.sku}
                              value={produto}
                            >
                              <span className="font-medium text-primary">{produto.sku}</span> <span>{produto.nome}</span>
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </>
                    )}
                  </ComboboxContent>
                </Combobox>
                <FieldError errors={conversaoErrors.codigo_produto ? [conversaoErrors.codigo_produto] : undefined} />
              </Field>

              <Field>
                <FieldLabel htmlFor="descricaoProduto">Descrição do Produto</FieldLabel>
                <Input
                  id="descricaoProduto"
                  type="text"
                  placeholder="Descrição (preenchida automaticamente)"
                  value={watchedDescricaoProduto}
                  readOnly
                  disabled
                />
              </Field>

              <Field data-invalid={!!conversaoErrors.unidade_origem}>
                <FieldLabel htmlFor="unidadeOrigem">Unidade de Origem *</FieldLabel>
                <div className="flex items-center gap-2">
                  <Controller
                    control={conversaoControl}
                    name="unidade_origem"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="unidadeOrigem"
                          className="flex-1"
                          aria-invalid={!!conversaoErrors.unidade_origem}
                        >
                          <SelectValue placeholder="Selecione…" />
                        </SelectTrigger>
                        <SelectContent>
                          {unidadesMedidaAgrupadas.map((grupo) => (
                            <SelectGroup key={grupo.grupo}>
                              <SelectLabel>{grupo.grupo}</SelectLabel>
                              {grupo.unidades.map((unidade) => (
                                <SelectItem
                                  key={unidade.value}
                                  value={unidade.value}
                                >
                                  {unidade.label} ({unidade.value})
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Adicionar nova unidade de medida"
                    title="Nova unidade"
                    onClick={() => setShowModalUnidade(true)}
                  >
                    <PlusIcon />
                  </Button>
                </div>
                <FieldError errors={conversaoErrors.unidade_origem ? [conversaoErrors.unidade_origem] : undefined} />
              </Field>

              <Field data-invalid={!!conversaoErrors.unidade_destino}>
                <FieldLabel htmlFor="unidadeDestino">Unidade de Destino *</FieldLabel>
                <div className="flex items-center gap-2">
                  <Controller
                    control={conversaoControl}
                    name="unidade_destino"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="unidadeDestino"
                          className="flex-1"
                          aria-invalid={!!conversaoErrors.unidade_destino}
                        >
                          <SelectValue placeholder="Selecione…" />
                        </SelectTrigger>
                        <SelectContent>
                          {unidadesMedidaAgrupadas.map((grupo) => (
                            <SelectGroup key={grupo.grupo}>
                              <SelectLabel>{grupo.grupo}</SelectLabel>
                              {grupo.unidades.map((unidade) => (
                                <SelectItem
                                  key={unidade.value}
                                  value={unidade.value}
                                >
                                  {unidade.label} ({unidade.value})
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Adicionar nova unidade de medida"
                    title="Nova unidade"
                    onClick={() => setShowModalUnidade(true)}
                  >
                    <PlusIcon />
                  </Button>
                </div>
                <FieldError errors={conversaoErrors.unidade_destino ? [conversaoErrors.unidade_destino] : undefined} />
              </Field>

              <div className="rounded-lg bg-surface-container p-4">
                <FieldLabel
                  htmlFor="valorConverter"
                  className="font-semibold text-on-surface"
                >
                  Calculadora de Conversão
                </FieldLabel>
                <Input
                  id="valorConverter"
                  type="number"
                  placeholder="Digite um valor para converter…"
                  value={valorConverter}
                  onChange={(e) => setValorConverter(e.target.value)}
                  step="any"
                  min="0"
                  autoComplete="off"
                  className="mt-2"
                />
                <div aria-live="polite">
                  {loadingConversao && <small className="mt-2 block text-primary">Calculando…</small>}
                  {resultadoConversao && valorConverter && watchedUnidadeOrigem && watchedUnidadeDestino && (
                    <div className="mt-3 rounded-lg bg-success/20 p-3">
                      <strong className="text-base text-on-surface">
                        {valorConverter} {watchedUnidadeOrigem} = {resultadoConversao.valor_convertido} {watchedUnidadeDestino}
                      </strong>
                      <br />
                      <small className="text-on-surface-variant">Fator utilizado: {resultadoConversao.fator_utilizado}</small>
                    </div>
                  )}
                  {!resultadoConversao && valorConverter && watchedUnidadeOrigem && watchedUnidadeDestino && !loadingConversao && (
                    <small className="mt-2 block text-error">Não foi possível calcular a conversão</small>
                  )}
                </div>
              </div>

              <Field data-invalid={!!conversaoErrors.fator_conversao}>
                <FieldLabel htmlFor="fatorConversao">Fator de Conversão *</FieldLabel>
                <Input
                  id="fatorConversao"
                  type="number"
                  placeholder="Ex: 1000 (1 KG = 1000 G)"
                  step="any"
                  min="0"
                  autoComplete="off"
                  aria-invalid={!!conversaoErrors.fator_conversao}
                  {...registerConversao('fator_conversao')}
                />
                <small className="text-on-surface-variant">Quantas unidades de destino equivalem a 1 unidade de origem</small>
                <FieldError errors={conversaoErrors.fator_conversao ? [conversaoErrors.fator_conversao] : undefined} />
              </Field>

              <Field>
                <FieldLabel htmlFor="unidadeVinculo">Unidade</FieldLabel>
                <Controller
                  control={conversaoControl}
                  name="unidade"
                  render={({ field }) => (
                    <Combobox
                      items={unidades}
                      itemToStringLabel={(unidade: IUnidade) => unidade.nome || 'Sem nome'}
                      value={unidades.find((unidade) => String(unidade.id) === field.value) ?? null}
                      onValueChange={(unidade: IUnidade | null) => field.onChange(unidade ? String(unidade.id) : '')}
                      disabled={singleUnidadeSelectLocked}
                    >
                      <ComboboxInput
                        id="unidadeVinculo"
                        placeholder={unidadesLoading ? 'Carregando unidades…' : 'Filtrar unidade…'}
                        autoComplete="off"
                        disabled={unidadesLoading || singleUnidadeSelectLocked}
                      />
                      <ComboboxContent>
                        <ComboboxEmpty>Nenhuma unidade encontrada</ComboboxEmpty>
                        <ComboboxList>
                          {(unidade: IUnidade) => (
                            <ComboboxItem
                              key={unidade.id}
                              value={unidade}
                            >
                              {unidade.nome || 'Sem nome'}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  )}
                />
                {unidadesError && <span className="text-sm text-destructive">Erro ao carregar unidades: {unidadesError}</span>}
              </Field>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeConversaoModal}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-success hover:bg-success/90"
                  disabled={saving || !isConversaoValid}
                >
                  {saving ? (
                    <>
                      <Spinner data-icon="inline-start" />
                      {editingConversao ? 'Salvando…' : 'Criando…'}
                    </>
                  ) : editingConversao ? (
                    'Salvar Alterações'
                  ) : (
                    'Criar Conversão'
                  )}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showModalUnidade}
        onOpenChange={(open) => {
          if (!open) closeNovaUnidadeModal();
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nova Unidade de Medida</DialogTitle>
          </DialogHeader>

          <form onSubmit={(e) => void handleNovaUnidadeSubmit(onSubmitNovaUnidade)(e)}>
            <FieldGroup>
              <Field data-invalid={!!novaUnidadeErrors.value}>
                <FieldLabel htmlFor="novaUnidadeValue">Código *</FieldLabel>
                <Controller
                  control={novaUnidadeControl}
                  name="value"
                  render={({ field }) => (
                    <Input
                      id="novaUnidadeValue"
                      type="text"
                      placeholder="Ex: KG, L, UN"
                      maxLength={20}
                      autoComplete="off"
                      aria-invalid={!!novaUnidadeErrors.value}
                      {...field}
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  )}
                />
                <FieldError errors={novaUnidadeErrors.value ? [novaUnidadeErrors.value] : undefined} />
              </Field>

              <Field data-invalid={!!novaUnidadeErrors.label}>
                <FieldLabel htmlFor="novaUnidadeLabel">Nome *</FieldLabel>
                <Input
                  id="novaUnidadeLabel"
                  type="text"
                  placeholder="Ex: Quilograma, Litro"
                  maxLength={100}
                  autoComplete="off"
                  aria-invalid={!!novaUnidadeErrors.label}
                  {...registerNovaUnidade('label')}
                />
                <FieldError errors={novaUnidadeErrors.label ? [novaUnidadeErrors.label] : undefined} />
              </Field>

              <Field data-invalid={!!novaUnidadeErrors.grupo}>
                <FieldLabel htmlFor="novaUnidadeGrupo">Grupo *</FieldLabel>
                <Controller
                  control={novaUnidadeControl}
                  name="grupo"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger
                        id="novaUnidadeGrupo"
                        aria-invalid={!!novaUnidadeErrors.grupo}
                      >
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {GRUPOS_DISPONIVEIS.map((g) => (
                            <SelectItem
                              key={g}
                              value={g}
                            >
                              {g}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                />
                <FieldError errors={novaUnidadeErrors.grupo ? [novaUnidadeErrors.grupo] : undefined} />
              </Field>

              <Field data-invalid={!!novaUnidadeErrors.unidade}>
                <FieldLabel htmlFor="novaUnidadeVinculo">Unidade *</FieldLabel>
                <Controller
                  control={novaUnidadeControl}
                  name="unidade"
                  render={({ field }) => (
                    <Combobox
                      items={unidades}
                      itemToStringLabel={(unidade: IUnidade) => unidade.nome || 'Sem nome'}
                      value={unidades.find((unidade) => String(unidade.id) === field.value) ?? null}
                      onValueChange={(unidade: IUnidade | null) => field.onChange(unidade ? String(unidade.id) : '')}
                      disabled={singleUnidadeSelectLocked}
                    >
                      <ComboboxInput
                        id="novaUnidadeVinculo"
                        placeholder={unidadesLoading ? 'Carregando unidades…' : 'Filtrar unidade…'}
                        autoComplete="off"
                        disabled={unidadesLoading || singleUnidadeSelectLocked}
                      />
                      <ComboboxContent>
                        <ComboboxEmpty>Nenhuma unidade encontrada</ComboboxEmpty>
                        <ComboboxList>
                          {(unidade: IUnidade) => (
                            <ComboboxItem
                              key={unidade.id}
                              value={unidade}
                            >
                              {unidade.nome || 'Sem nome'}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>
                    </Combobox>
                  )}
                />
                {unidadesError && <span className="text-sm text-destructive">Erro ao carregar unidades: {unidadesError}</span>}
                <FieldError errors={novaUnidadeErrors.unidade ? [novaUnidadeErrors.unidade] : undefined} />
              </Field>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeNovaUnidadeModal}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-success hover:bg-success/90"
                  disabled={!isNovaUnidadeValid || criarUnidadeMutation.isPending}
                >
                  {criarUnidadeMutation.isPending ? (
                    <>
                      <Spinner data-icon="inline-start" />
                      Salvando…
                    </>
                  ) : (
                    'Salvar'
                  )}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showModalGerenciarUnidades}
        onOpenChange={setShowModalGerenciarUnidades}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerenciar Unidades de Medida</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-on-surface-variant">
            Unidades padrão do sistema não podem ser excluídas. Apenas unidades cadastradas por você.
          </p>

          <div className="max-h-[60vh] overflow-auto overscroll-contain">
            {unidadesMedidaAgrupadas.map((grupo) => (
              <div
                key={grupo.grupo}
                className="mb-5"
              >
                <h4 className="border-b border-outline-variant pb-1.5 font-semibold text-on-surface">{grupo.grupo}</h4>
                <Table>
                  <TableBody>
                    {grupo.unidades.map((unidade) => (
                      <TableRow key={unidade.value}>
                        <TableCell>
                          <strong>{unidade.value}</strong> - {unidade.label}
                        </TableCell>
                        <TableCell className="text-right">
                          {podeExcluirUnidade(unidade) ? (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => setDeleteUnidadeConfirm({ value: unidade.value, label: unidade.label })}
                            >
                              Excluir
                            </Button>
                          ) : (
                            <span className="text-xs text-on-surface-variant">Padrão</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowModalGerenciarUnidades(false)}
            >
              Fechar
            </Button>
            <Button
              onClick={() => {
                setShowModalGerenciarUnidades(false);
                setShowModalUnidade(true);
              }}
            >
              Nova Unidade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConversaoConfirmId != null}
        onOpenChange={(open) => {
          if (!open) setDeleteConversaoConfirmId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-on-surface-variant">Tem certeza que deseja excluir esta conversão? Esta ação não pode ser desfeita.</p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConversaoConfirmId(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmDeleteConversao()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Spinner data-icon="inline-start" />
                  Excluindo…
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteUnidadeConfirm != null}
        onOpenChange={(open) => {
          if (!open) setDeleteUnidadeConfirm(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-on-surface-variant">
            Tem certeza que deseja excluir a unidade &ldquo;{deleteUnidadeConfirm?.label} ({deleteUnidadeConfirm?.value})&rdquo;? Esta ação não pode ser
            desfeita.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteUnidadeConfirm(null)}
              disabled={excluirUnidadeMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmExcluirUnidade()}
              disabled={excluirUnidadeMutation.isPending}
            >
              {excluirUnidadeMutation.isPending ? (
                <>
                  <Spinner data-icon="inline-start" />
                  Excluindo…
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
